import { CaretDownIcon, IconContext } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { ButtonHTMLAttributes, ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import { NavLink } from 'react-router';
import styles from './NavItem.module.scss';

interface INavItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon?: ReactNode;
  selected?: boolean;
  dropdown?: boolean;
  to?: string;
  end?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const ICON_SIZE = 14;

export function NavItem({
  label,
  icon,
  selected,
  dropdown,
  to,
  end,
  className,
  disabled,
  ref,
  ...rest
}: INavItemProps) {
  'use memo';

  const rootClass = (isSelected: boolean) => classNames(styles.navItem, { [styles.selected]: isSelected }, className);

  const content = (
    <>
      {icon && (
        <span className={styles.icon}>
          <IconContext.Provider value={{ weight: 'bold', size: ICON_SIZE }}>{icon}</IconContext.Provider>
        </span>
      )}
      <span className={styles.label}>{label}</span>
      {dropdown && <CaretDownIcon size={16} weight="bold" className={styles.caret} />}
    </>
  );

  if (to) {
    return (
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) => rootClass(selected ?? isActive)}
        {...(rest as Omit<ComponentPropsWithoutRef<typeof NavLink>, 'to' | 'className' | 'children'>)}
      >
        {content}
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      ref={ref}
      className={rootClass(!!selected)}
      disabled={disabled}
      aria-pressed={selected}
      {...rest}
    >
      {content}
    </button>
  );
}
